import type { EditorCommandHost } from "@/editor/commands/editorCommandHost";
import {
  addPenVertex,
  bendVectorEdge,
  connectPenToInsertedVertex,
  connectPenVertices,
  createPenNetwork,
  duplicateVectorVerticesForDrag,
  insertVectorVertexAtIntersection,
  insertVectorVertexOnEdge,
  knifeCutAroundVertex,
  knifeCutEdges,
  moveVectorEdgeVertices,
  moveVectorHandle,
  moveVectorVertex,
  sculptVectorVertices,
  extrudeSelectedEdges,
  subdivideSelectedEdges,
  toggleVectorRegionFill,
} from "@/editor/commands";
import {
  cancelPathDrawing,
  enterEditMode,
  enterVectorMode,
  exitVectorMode,
} from "@/editor/shell/VectorEditSession";
import { deleteSelectedVectorSelection } from "@/editor/commands";
import { bindCommand } from "@/editor/shell/shellCommandBinding";
import type { VectorCanvasActions } from "./types";

export interface VectorActionDeps {
  getActivePageId: () => string | undefined;
  textEditSessionRef: import("@/editor/shell/TextEditSession").TextEditSessionRef;
  getPrimarySelectedNodeId: () => string | undefined;
  onEnterVectorMode: () => void;
  onExitVectorMode: () => void;
  onSelectedVertexChange: VectorCanvasActions["onSelectedVertexChange"];
  onSelectedVerticesChange: VectorCanvasActions["onSelectedVerticesChange"];
  onSelectedEdgesChange: VectorCanvasActions["onSelectedEdgesChange"];
  onSelectedHandleChange: VectorCanvasActions["onSelectedHandleChange"];
  onShapeBuilderCommit: VectorCanvasActions["onShapeBuilderCommit"];
  onPencilDraw: VectorCanvasActions["onPencilDraw"];
}

export function createVectorActions(host: EditorCommandHost, deps: VectorActionDeps): VectorCanvasActions {
  const { getActivePageId, textEditSessionRef, getPrimarySelectedNodeId } = deps;
  return {
    onEnterVectorMode: deps.onEnterVectorMode,
    onExitVectorMode: deps.onExitVectorMode,
    onSelectedVertexChange: deps.onSelectedVertexChange,
    onSelectedVerticesChange: deps.onSelectedVerticesChange,
    onSelectedEdgesChange: deps.onSelectedEdgesChange,
    onSelectedHandleChange: deps.onSelectedHandleChange,
    onVertexCommit: bindCommand(host, moveVectorVertex),
    onEdgeMoveCommit: bindCommand(host, moveVectorEdgeVertices),
    onHandleCommit: bindCommand(host, moveVectorHandle),
    onEdgeBendCommit: bindCommand(host, bendVectorEdge),
    onKnifeCutEdges: bindCommand(host, knifeCutEdges),
    onKnifeCutAroundVertex: bindCommand(host, knifeCutAroundVertex),
    onVectorRegionFillToggle: bindCommand(host, toggleVectorRegionFill),
    onShapeBuilderCommit: deps.onShapeBuilderCommit,
    onSculptCommit: bindCommand(host, sculptVectorVertices),
    onSubdivideCommit: bindCommand(host, subdivideSelectedEdges),
    onExtrudeCommit: bindCommand(host, extrudeSelectedEdges),
    onPathInsertVertex: bindCommand(host, insertVectorVertexOnEdge),
    onPathInsertIntersection: bindCommand(host, insertVectorVertexAtIntersection),
    onPathToolStart: (point) => createPenNetwork(host, point, { getActivePageId }),
    onPenAddVertex: bindCommand(host, addPenVertex),
    onPenConnectVertices: bindCommand(host, connectPenVertices),
    onPenConnectToInsertedVertex: bindCommand(host, connectPenToInsertedVertex),
    onPencilDraw: deps.onPencilDraw,
    onDuplicateVerticesDragStart: bindCommand(host, duplicateVectorVerticesForDrag),
  };
}

export function enterVectorModeFromHost(host: EditorCommandHost, nodeId?: string): void {
  enterVectorMode(host, nodeId);
}

export function exitVectorModeFromHost(host: EditorCommandHost): void {
  exitVectorMode(host);
}

export function enterEditModeFromHost(
  host: EditorCommandHost,
  textEditSessionRef: VectorActionDeps["textEditSessionRef"],
  primarySelectedNodeId?: string,
): void {
  enterEditMode(host, textEditSessionRef, primarySelectedNodeId);
}

export function cancelPathDrawingFromHost(host: EditorCommandHost): boolean {
  return cancelPathDrawing(host);
}

export function deleteSelectedVectorSelectionFromHost(
  host: EditorCommandHost,
  options: { reconnectVertices?: boolean } = {},
): boolean {
  return deleteSelectedVectorSelection(host, options);
}
